import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import {
    emailBindingSchema,
    type EmailBindingFormValues,
} from '@/entities/EmailVerification/model/schema';
import type { EmailBindingTarget } from '@/entities/EmailVerification/model/types';
import { RequestStatus } from '@/shared/model/requestStatus';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { useProfileEmailBinding } from './useProfileEmailBinding';
import classes from './ProfileEmailBindingModal.module.scss';

interface ProfileEmailBindingModalProps {
    target: EmailBindingTarget;
    initialEmail?: string | null;
    onHide: () => void;
    onChanged: () => void | Promise<void>;
}

const { TextField: EmailField } = createRHFFields<EmailBindingFormValues>();

/** Показывает форму привязки почты клиента или исполнителя */
export const ProfileEmailBindingModal = (
    {
        target,
        initialEmail = '',
        onHide,
        onChanged,
    }: ProfileEmailBindingModalProps
) => {
    const form = useForm<EmailBindingFormValues>({
        resolver: zodResolver(emailBindingSchema),
        defaultValues: { email: initialEmail ?? '' },
    });

    const binding = useProfileEmailBinding(form, target, initialEmail ?? '');
    const isSubmitting = form.formState.isSubmitting;

    /** Закрывает форму привязки почты */
    const handleHide = () => {
        if (isSubmitting) return;

        onHide();

        if (binding.sentEmail != null) void onChanged();
    };

    const sendLabel = binding.secondsBeforeResend > 0
        ? `Повторить через ${binding.secondsBeforeResend} с`
        : binding.isVerifiedEmail
            ? 'Почта подтверждена'
            : binding.hasEmail && binding.isStoredEmail
                ? 'Отправить повторно'
                : 'Отправить письмо';

    return <Modal
        visible
        onHide={handleHide}
        title="Привязать почту"
        size="md"
        closable={!isSubmitting}
        className={classes.modal}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label={binding.sentEmail != null
                    ? 'Закрыть'
                    : 'Отмена'
                }
                text
                onClick={handleHide}
                disabled={isSubmitting}
            />
            <Button
                type="submit"
                form="profile-email-binding-form"
                label={sendLabel}
                icon="pi pi-envelope"
                loading={isSubmitting}
                disabled={!binding.canSend || isSubmitting}
            />
        </div>}
    >
        <FormProvider {...form}>
            <form
                id="profile-email-binding-form"
                onSubmit={form.handleSubmit(binding.onSubmit)}
                className={classes.form}
                noValidate
            >
                <p>Клиент или исполнитель сможет подтвердить адрес по ссылке в письме.</p>
                <EmailField
                    name="email"
                    type="email"
                    label="Электронная почта"
                    placeholder="example@mail.ru"
                    autoComplete="email"
                    isRequired
                    disabled={isSubmitting}
                    maxLength={254}
                />
                {binding.isVerifiedEmail &&
                    <Message
                        severity="success"
                        text="Этот адрес уже подтверждён"
                    />
                }
                {binding.sentEmail != null && !binding.isVerifiedEmail &&
                    <Message
                        severity="success"
                        text={`Запрос подтверждения отправлен на ${binding.sentEmail}. Откройте письмо и перейдите по ссылке.`}
                    />
                }
                {binding.requestStatus === RequestStatus.Loading &&
                    <RequestLoading message="Проверяем состояние почты..." />
                }
                {binding.requestStatus === RequestStatus.Error &&
                    <RequestError
                        message={binding.statusError ?? 'Не удалось получить состояние почты'}
                        onRetry={binding.refreshStatus}
                        retryLabel="Обновить состояние"
                        compact
                    />
                }
                {form.formState.errors.root?.server?.message &&
                    <RequestError
                        message={form.formState.errors.root.server.message}
                        compact
                    />
                }
            </form>
        </FormProvider>
    </Modal>;
};
